// Generic skeleton placeholder. Use while async data is loading so the
// page reserves its final layout instead of flashing "Loading…" text.
//
// Usage:
//   ListLoading: {
//     extends: 'Skeleton',
//     width: '100%', height: 'A',  // override size to match final content
//     round: 'Y'
//   }
//
//   Plus the SkeletonRow / SkeletonCircle helpers for common shapes.
//
// Animation: a gentle opacity pulse via brand's `pulse` keyframe
// (company/packages/brand/designSystem/animation.js). That keyframe did not
// actually exist until 2026-08-08 — this file had named it since it was
// written, and an unknown animation-name is silently inert in CSS, so every
// skeleton in the product was a flat static block and the reduced-motion
// branch below had no motion to suppress. Both are live now.

export const Skeleton = {
  display: 'block',
  width: '100%',
  // §9 zero-shift law: a skeleton must mirror the geometry of the real
  // content it stands in for. `V` (~1.5px) measured invisible in the live
  // app (DESIGN-LOCKS-RED-1 browser check) — no real line of text is
  // 1.5px tall. `Z` sits in this design system's line-height range.
  height: 'Z',
  round: 'radiusPill',
  // `overlay` is an ink-matching-background wash (gray14/gray2 — the SAME
  // base gray a dark-theme modal panel already is), so a skeleton inside a
  // Modal composited to zero visible contrast (measured live, DESIGN-LOCKS-
  // RED-1 browser check: fill and panel both rgb(20,20,22)). `selected`
  // washes the OPPOSITE tone (black/white alpha, like `hover`), so it reads
  // against either theme's panel regardless of the panel's own shade.
  background: 'selected',
  // shimmer animation — gentle 1.5s loop
  animation: 'pulse 1.5s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: '0.6'
  }
}

// One-line text placeholder. Stack multiple to fake a paragraph.
export const SkeletonRow = {
  extends: 'Skeleton',
  height: 'Z'
}

// Avatar / icon placeholder.
export const SkeletonCircle = {
  extends: 'Skeleton',
  width: 'B',
  height: 'B',
  // the shape token (was the raw '50%'): a pill on a square box is a circle
  round: 'radiusPill'
}

// Card-shaped placeholder — title + 2 lines. Borderless (fill contrast
// separates — DESIGN_LANGUAGE.md §2.5); card-class radius.
export const SkeletonCard = {
  flow: 'y',
  gap: 'V',
  padding: 'A',
  round: 'radiusCard',
  // The SURFACE of the card it stands for, too (Rule 66): the brand `card`
  // theme, the raised surface one step off a sheet — 1.13:1 light, 1.19:1
  // dark, and its `selected` rows step on it. It was `overlay`, a 5% wash
  // of the ground's own family: 1.01:1 on the sheet in light and 1.00:1 in
  // dark, so the card vanished and only its rows showed (filed by admin fix
  // lane B, 2026-09-24, which had to force `theme: 'card'` on the page).
  theme: 'card',

  Title: { extends: 'SkeletonRow', width: '60%', height: 'Z2' },
  Line_1: { extends: 'SkeletonRow', width: '100%' },
  Line_2: { extends: 'SkeletonRow', width: '80%' }
}
